import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, GripVertical, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AdminLayout from '../../components/AdminLayout';
import { getPackages, createPackage, updatePackage, deletePackage, reorderPackages } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const SortablePackageItem = ({ pkg, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: pkg.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="admin-card flex items-start justify-between"
      data-testid={`package-item-${pkg.id}`}
    >
      <div className="flex gap-4">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing mt-1"
          data-testid={`drag-handle-${pkg.id}`}
        >
          <GripVertical className="w-5 h-5 text-neutral-300 hover:text-neutral-500" />
        </button>
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-lg">{pkg.name}</h3>
            {pkg.is_popular && (
              <span className="bg-neutral-900 text-white text-xs px-2 py-0.5 rounded">
                Popular
              </span>
            )}
          </div>
          <p className="text-2xl font-bold text-neutral-900 mt-1">{pkg.price}</p>
          <p className="text-neutral-500 mt-2">{pkg.description}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {pkg.features.slice(0, 4).map((feature, idx) => (
              <span key={idx} className="bg-neutral-100 text-neutral-600 text-sm px-3 py-1 rounded">
                {feature}
              </span>
            ))}
            {pkg.features.length > 4 && (
              <span className="text-neutral-500 text-sm">+{pkg.features.length - 4} more</span>
            )}
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <Button 
          variant="outline" 
          size="icon"
          onClick={() => onEdit(pkg)}
          data-testid={`edit-package-${pkg.id}`}
        >
          <Pencil className="w-4 h-4" />
        </Button>
        <Button 
          variant="outline" 
          size="icon"
          className="text-red-600 hover:text-red-700"
          onClick={() => onDelete(pkg)}
          data-testid={`delete-package-${pkg.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

const AdminPackages = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    description: '',
    features: [''],
    is_popular: false,
    order: 0
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const res = await getPackages();
      setPackages(res.data);
    } catch (error) {
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      const oldIndex = packages.findIndex((p) => p.id === active.id);
      const newIndex = packages.findIndex((p) => p.id === over.id);
      
      const newPackages = arrayMove(packages, oldIndex, newIndex);
      setPackages(newPackages);
      
      // Update order in backend
      const reorderData = newPackages.map((pkg, index) => ({
        id: pkg.id,
        order: index
      }));
      
      try {
        await reorderPackages(reorderData);
        toast.success('Order updated');
      } catch (error) {
        toast.error('Failed to update order');
        fetchPackages(); // Revert on error
      }
    }
  };

  const handleOpenDialog = (pkg = null) => {
    if (pkg) {
      setFormData({
        name: pkg.name,
        price: pkg.price,
        description: pkg.description,
        features: pkg.features.length > 0 ? pkg.features : [''],
        is_popular: pkg.is_popular,
        order: pkg.order
      });
      setSelectedPackage(pkg);
    } else {
      setFormData({
        name: '',
        price: '',
        description: '',
        features: [''],
        is_popular: false,
        order: packages.length
      });
      setSelectedPackage(null);
    }
    setDialogOpen(true);
  };

  const handleAddFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, '']
    }));
  };

  const handleRemoveFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleFeatureChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.map((f, i) => i === index ? value : f)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const data = {
      ...formData,
      features: formData.features.filter(f => f.trim() !== '')
    };

    try {
      if (selectedPackage) {
        await updatePackage(selectedPackage.id, data);
        toast.success('Package updated successfully');
      } else {
        await createPackage(data);
        toast.success('Package created successfully');
      }
      setDialogOpen(false);
      fetchPackages();
    } catch (error) {
      toast.error('Failed to save package');
    }
  };

  const handleDelete = async () => {
    try {
      await deletePackage(selectedPackage.id);
      toast.success('Package deleted successfully');
      setDeleteDialogOpen(false);
      fetchPackages();
    } catch (error) {
      toast.error('Failed to delete package');
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-packages-page">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
              Packages
            </h1>
            <p className="text-neutral-500 mt-2">Manage your service packages (drag to reorder)</p>
          </div>
          <Button 
            onClick={() => handleOpenDialog()}
            className="bg-neutral-900 hover:bg-neutral-800"
            data-testid="add-package-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Package
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900"></div>
          </div>
        ) : packages.length === 0 ? (
          <div className="admin-card text-center py-16">
            <p className="text-neutral-500 mb-4">No packages yet</p>
            <Button onClick={() => handleOpenDialog()}>Create your first package</Button>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={packages.map(p => p.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="grid gap-4">
                {packages.map((pkg) => (
                  <SortablePackageItem
                    key={pkg.id}
                    pkg={pkg}
                    onEdit={handleOpenDialog}
                    onDelete={(pkg) => {
                      setSelectedPackage(pkg);
                      setDeleteDialogOpen(true);
                    }}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        {/* Package Form Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {selectedPackage ? 'Edit Package' : 'Add Package'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Package Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Basic Package"
                    required
                    data-testid="package-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price">Price</Label>
                  <Input
                    id="price"
                    value={formData.price}
                    onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                    placeholder="e.g., $500"
                    required
                    data-testid="package-price-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of the package"
                  rows={3}
                  data-testid="package-description-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Features</Label>
                <div className="space-y-2">
                  {formData.features.map((feature, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={feature}
                        onChange={(e) => handleFeatureChange(index, e.target.value)}
                        placeholder="e.g., 4 hours of service"
                        data-testid={`package-feature-input-${index}`}
                      />
                      {formData.features.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => handleRemoveFeature(index)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handleAddFeature}
                    className="w-full"
                    data-testid="add-feature-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Feature
                  </Button>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Switch
                  id="is_popular"
                  checked={formData.is_popular}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_popular: checked }))}
                  data-testid="package-popular-switch"
                />
                <Label htmlFor="is_popular">Mark as popular</Label>
              </div>

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-neutral-900 hover:bg-neutral-800" data-testid="save-package-btn">
                  {selectedPackage ? 'Update Package' : 'Create Package'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Package</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{selectedPackage?.name}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-btn"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminPackages;
